import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  styled,
  IconButton,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';

// Components und Interfaces
import WithFeedbackTabData from "ui/containers/settings/FeedbackTabContainer";
import { FeedbackTabPropType } from "entities/interfaces";
import { SubmitButton } from "ui/components/shared/SubmitButton";

// === Mobile Header Component ===
const MobileHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '0.75rem 1rem',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '2rem',
  gap: '1rem',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'sticky',
  top: 0,
  zIndex: 100,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  flex: 1,
}));

// === Mobile Container ===
const MobileContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
}));

// === Content Wrapper ===
const ContentWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
}));

// === Mobile Card ===
const MobileCard = styled(Paper)(({ theme }) => ({
  flex: 1,
  borderRadius: "12px",
  padding: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.common.white,
  minHeight: 0,
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const FeedbackForm = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

const TextFieldWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
}));

const SendButtonContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '0.5rem',
}));

// === Component ===
const MobileFeedbackSettings = ({ feedbackData, addFeedback }: FeedbackTabPropType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!feedbackText.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      // Sende Feedback ans Backend
      await addFeedback(feedbackText);
      
      // Success State
      setShowSuccess(true);
      setFeedbackText("");
      
      // Success Message nach 3 Sekunden ausblenden
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error("Fehler beim Senden des Feedbacks:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MobileContainer>
      {/* Mobile Header */}
      <MobileHeader>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <HeaderTitle>{t("Feedback")}</HeaderTitle>
      </MobileHeader>

      {/* Content */}
      <ContentWrapper>
        <MobileCard>
          {/* Header Section */}
          <Box>
            <Title>{t("Feedback")}</Title>
            <Subtitle>{t("Let us know what you think about your experience.")}</Subtitle>
          </Box>

          {/* Success Alert */}
          {showSuccess && (
            <Alert 
              severity="success" 
              sx={{ 
                borderRadius: '8px',
                fontSize: '0.875rem',
              }}
            >
              {t("Thank you for your feedback!")}
            </Alert>
          )}

          {/* Feedback Form */}
          <FeedbackForm>
            <TextFieldWrapper>
              <StyledTextField
                multiline
                rows={6}
                placeholder={t("Share your thoughts, suggestions, or report issues...")}
                value={feedbackText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFeedbackText(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                fullWidth
              />
              
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: '0.75rem', textAlign: 'right' }}
              >
                {feedbackText.length}/500
              </Typography>
            </TextFieldWrapper>

            <SendButtonContainer>
              <SubmitButton
                color="primary"
                onClick={handleSubmit}
                disabled={!feedbackText.trim() || isSubmitting}
                startIcon={<SendIcon />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  padding: '0.75rem 1.5rem',
                }}
              >
                {isSubmitting ? t("Sending...") : t("Send Feedback")}
              </SubmitButton>
            </SendButtonContainer>
          </FeedbackForm>
        </MobileCard>
      </ContentWrapper>
    </MobileContainer>
  );
};

export default WithFeedbackTabData(MobileFeedbackSettings);